import React, { useState, useMemo } from 'react';
import { Box, Typography, TextField, Select, MenuItem, FormControl, Card, Grid, ToggleButtonGroup, ToggleButton, Button, Chip } from '@mui/material';
import useRscPricing from '../../hooks/useRscPricing';
import useCscBillProposal from '../../hooks/useCscBillProposal';

const PP = "'Poppins', sans-serif";
const COLORS = {
    primary: '#1A8CFF', dark: '#0F62CC', light: '#E8F4FF', bg: '#F8FAFC', card: '#FFFFFF',
    text: '#0F172A', sub: '#64748B', border: '#E2E8F0', success: '#10B981', warning: '#F59E0B',
};
const Label = ({ children, required }) => (
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: PP }}>
        {children}{required && <span style={{ color: '#EF4444' }}> *</span>}
    </Typography>
);

const TARIFFS = [
    { rate: 0.515, label: 'Low Voltage (LV)' },
    // Time-of-Use LV: peak/off-peak energy, capacity and network per kWh, and a
    // rebate line. Priced by calcCommercialLvTouBill, never bill ÷ rate.
    { rate: 0.4539, label: 'Low Voltage ToU (LV ToU)' },
    { rate: 0.303, label: 'Medium Voltage (MV)' },
    { rate: 0.437, label: 'High Voltage (HV)' },
    { rate: 0.451, label: 'Agriculture' },
];

const HOUR_PRESETS = [
    { key: '9-17',  label: '9:00 — 17:00 (Office)',         start: 9,  end: 17 },
    { key: '8-17',  label: '8:00 — 17:00 (Day Shift)',      start: 8,  end: 17 },
    { key: '8-18',  label: '8:00 — 18:00 (Extended)',       start: 8,  end: 18 },
    { key: '10-22', label: '10:00 — 22:00 (Retail / F&B)',  start: 10, end: 22 },
    { key: '24h',   label: '24-Hour Operation',             start: 0,  end: 24 },
    { key: 'custom', label: 'Custom — choose hours below',  start: 9,  end: 17 },
];

function ProposalCard({ data, accent, headerLabel, fmt, SpecRow, GroupLabel }) {
    const isSelco = data.mode === 'selco';
    const stApplies = !!data.stLicenseApplies;
    return (
        <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden', borderTop: `4px solid ${accent}`, height: '100%' }}>
            <Box sx={{ px: 1.75, py: 1.25, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#F8FAFC' }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: accent, fontFamily: PP }}>{headerLabel}</Typography>
                <Chip label={`${data.kwp} kWp`} size="small" sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.68rem', height: 22, borderRadius: 6, backgroundColor: '#F1F5F9', color: COLORS.text }} />
            </Box>
            <Box sx={{ px: 1.75, pt: 0.5, pb: 1.5 }}>
                <GroupLabel>System</GroupLabel>
                <SpecRow label="Panels" value={`${data.panels} × ${data.panels ? Math.round(Number(data.kwp) * 1000 / Number(data.panels)) : 650}Wp`} />
                <SpecRow label="Inverter" value={`${data.inverterKw} kW · ${data.brandLabel}${isSelco ? ' · Zero Export' : ''}`} />
                <SpecRow label="Price" value={fmt(data.cashPrice)} />

                <GroupLabel>Energy Flow (Monthly)</GroupLabel>
                <SpecRow label="Generated" value={`${Math.round(data.generated || 0).toLocaleString()} kWh`} color={COLORS.sub} />
                <SpecRow label="Self-consumed" value={`${Math.round(data.selfConsumed || 0).toLocaleString()} kWh`} color={COLORS.text} />
                <SpecRow label="Import (after solar)" value={`${Math.round(data.importAfterSolar || 0).toLocaleString()} kWh`} color={COLORS.text} />
                {isSelco ? (
                    <SpecRow label="Curtailed" value={`${Math.round(data.curtailed || 0).toLocaleString()} kWh (${Math.round((data.curtailedPct || 0) * 100)}%)`} color={(data.curtailedPct || 0) > 0.30 ? COLORS.warning : COLORS.text} />
                ) : (
                    <SpecRow label="Exported (credited)" value={`${Math.round(data.exported || 0).toLocaleString()} kWh (${Math.round(data.exportPct * 100)}%)`} color={data.exportPct > 0.30 ? COLORS.warning : COLORS.text} />
                )}

                <GroupLabel>Monthly Saving</GroupLabel>
                <SpecRow label="Theoretical Saving" value={fmt(data.originalSaving)} color={COLORS.sub} />
                <SpecRow
                    label={isSelco ? 'Self-consumed' : 'After export'}
                    value={fmt(stApplies ? data.grossMonthlySaving : data.monthlySaving)}
                    color={COLORS.success}
                />
                {stApplies && (
                    <>
                        <SpecRow label={`ST License (>${72} kW)`} value={`− ${fmt(data.monthlyLicenseFee)} / mo`} color={COLORS.warning} />
                        <SpecRow label="Net Saving" value={fmt(data.monthlySaving)} color={COLORS.success} />
                    </>
                )}

                <GroupLabel>Customer Value</GroupLabel>
                <SpecRow label="% of Bill Saved" value={`${Math.round(data.savingPct * 100)}%`} color={accent} />
                <SpecRow label={stApplies ? 'ROI (incl. License)' : 'ROI'} value={data.roiYears != null ? `${data.roiYears} yrs` : '—'} color={accent} />
                {stApplies && (
                    <SpecRow label="ROI (excl. License)" value={data.roiYearsGross != null ? `${data.roiYearsGross} yrs` : '—'} color={COLORS.sub} />
                )}
                <SpecRow label="10-Year Saving" value={fmt(data.monthlySaving * 120)} color={COLORS.success} />
                <SpecRow label="Net Return (10-Yr)" value={fmt(data.monthlySaving * 120 - data.cashPrice)} color={(data.monthlySaving * 120 - data.cashPrice) >= 0 ? COLORS.success : COLORS.warning} />
                <SpecRow label="20-Year Saving" value={fmt(data.monthlySaving * 240)} color={COLORS.success} />
                <SpecRow label="Net Return (20-Yr)" value={fmt(data.monthlySaving * 240 - data.cashPrice)} color={(data.monthlySaving * 240 - data.cashPrice) >= 0 ? COLORS.success : COLORS.warning} />
            </Box>
        </Card>
    );
}

export default function CscBillTab() {
    const [companyName, setCompanyName] = useState('');
    const [bill, setBill] = useState('');
    const [tariffRate, setTariffRate] = useState(0.515);
    const [hoursPreset, setHoursPreset] = useState('custom');
    const [opStart, setOpStart] = useState(9);
    const [opEnd, setOpEnd] = useState(17);
    const [operatingDays, setOperatingDays] = useState(5);
    const [offHourBaselinePct, setOffHourBaselinePct] = useState(20);  // % of monthly consumption outside op hours
    const [maxDemand, setMaxDemand] = useState('');
    const [phaseMode, setPhaseMode] = useState('3phase');
    const [brand, setBrand] = useState('goodwe');
    const [lang, setLang] = useState('en');
    const [copyText, setCopyText] = useState('📋 Copy Message');
    const [isCopied, setIsCopied] = useState(false);

    const { atapPrices } = useRscPricing('Residential Pricing', phaseMode, 999);

    const { proposals, breakdown } = useCscBillProposal({
        residentialRows: atapPrices,
        monthlyBill: parseFloat(bill) || 0,
        tariffRate,
        operatingDays,
        opStart, opEnd,
        maxDemandKw: parseFloat(maxDemand) || 0,
        phaseMode,
        brand,
        offHourBaselinePct: (Number(offHourBaselinePct) || 0) / 100,
    });
    const { maxSaving, fastestRoi, targetFifty, selco } = proposals;
    const [selected, setSelected] = useState('maxSaving');
    const pick = (() => {
        if (selected === 'fastestRoi' && fastestRoi) return fastestRoi;
        if (selected === 'targetFifty' && targetFifty) return targetFifty;
        if (selected === 'selco' && selco) return selco;
        return maxSaving;
    })();
    const huaweiForced = pick && pick.brand === 'huawei' && brand !== 'huawei';

    const handlePreset = (key) => {
        const preset = HOUR_PRESETS.find(p => p.key === key);
        if (!preset) return;
        setHoursPreset(key);
        if (key !== 'custom') {
            setOpStart(preset.start);
            setOpEnd(preset.end);
        }
    };

    const fmt = (n) => 'RM ' + Number(n || 0).toLocaleString('en-MY', { maximumFractionDigits: 0 });
    const fmtKwh = (n) => Number(n || 0).toLocaleString('en-MY', { maximumFractionDigits: 0 }) + ' kWh';
    const tariffLabel = TARIFFS.find(t => t.rate === tariffRate)?.label || `RM ${tariffRate}/kWh`;
    const phaseLabel = phaseMode === '3phase' ? '3-Phase' : '1-Phase';
    const fmtHr = (h) => `${String(h).padStart(2, '0')}:00`;
    const hoursLabel = opStart === 0 && opEnd === 24 ? '24-Hour Operation' : `${fmtHr(opStart)} — ${fmtHr(opEnd)}`;

    const billKwh = breakdown?.monthlyKwh > 0 ? breakdown.monthlyKwh : 0;

    const message = useMemo(() => {
        if (!pick) return '';
        const isCN = lang === 'zh', isMY = lang === 'ms';
        const t = (en, cn, my) => isCN ? cn : isMY ? my : en;
        const co = companyName ? companyName.toUpperCase() + '\n' : '';
        const lines = [];

        lines.push(t('🏭 *Commercial Solar Proposal*', '🏭 *商用太阳能方案*', '🏭 *Cadangan Solar Komersial*'));
        if (co) lines.push(co.trim());
        lines.push('');

        lines.push(t('📋 *Site Profile*', '📋 *客户需求*', '📋 *Profil Tapak*'));
        lines.push(t(`• Monthly Bill: ${fmt(parseFloat(bill))} (${tariffLabel})`,
                     `• 月费: ${fmt(parseFloat(bill))} (${tariffLabel})`,
                     `• Bil Bulanan: ${fmt(parseFloat(bill))} (${tariffLabel})`));
        lines.push(t(`• Monthly Usage: ${fmtKwh(billKwh)}`,
                     `• 月用电量: ${fmtKwh(billKwh)}`,
                     `• Penggunaan Bulanan: ${fmtKwh(billKwh)}`));
        lines.push(t(`• Operating: ${hoursLabel}, ${operatingDays} days/week`,
                     `• 营运时间: ${hoursLabel}, 每周 ${operatingDays} 天`,
                     `• Operasi: ${hoursLabel}, ${operatingDays} hari/minggu`));
        lines.push(t(`• Max Demand: ${maxDemand} kW`,
                     `• 最大需量: ${maxDemand} kW`,
                     `• Permintaan Maksimum: ${maxDemand} kW`));
        lines.push(t(`• Phase: ${phaseLabel}`,
                     `• 相数: ${phaseLabel}`,
                     `• Fasa: ${phaseLabel}`));
        lines.push('');

        const isSelco = pick.mode === 'selco';
        const proposalHeader = isSelco
            ? t('☀️ *Our Proposal — SELCo (Zero Export)*', '☀️ *我们的建议 — SELCo (零出口)*', '☀️ *Cadangan Kami — SELCo (Tiada Eksport)*')
            : t('☀️ *Our Proposal — ATAP (with Export)*', '☀️ *我们的建议 — ATAP (含出口)*', '☀️ *Cadangan Kami — ATAP (dengan Eksport)*');
        lines.push(proposalHeader);
        const pickWatt = pick.panels ? Math.round(Number(pick.kwp) * 1000 / Number(pick.panels)) : 650;
        lines.push(t(`• System: *${pick.kwp} kWp* (${pick.panels} panels × ${pickWatt}Wp)`,
                     `• 系统: *${pick.kwp} kWp* (${pick.panels} 块电板 × ${pickWatt}Wp)`,
                     `• Sistem: *${pick.kwp} kWp* (${pick.panels} panel × ${pickWatt}Wp)`));
        const inverterSuffix = isSelco
            ? t(' · Zero-Export Mode', ' · 零出口模式', ' · Mod Tanpa Eksport')
            : '';
        lines.push(t(`• Inverter: ${pick.inverterKw} kW ${pick.brandLabel} ${pick.brandType} (${phaseLabel})${inverterSuffix}`,
                     `• 逆变器: ${pick.inverterKw} kW ${pick.brandLabel} (${phaseLabel})${inverterSuffix}`,
                     `• Inverter: ${pick.inverterKw} kW ${pick.brandLabel} (${phaseLabel})${inverterSuffix}`));
        lines.push(t(`• Total Price: *${fmt(pick.cashPrice)}*`,
                     `• 总价: *${fmt(pick.cashPrice)}*`,
                     `• Harga Penuh: *${fmt(pick.cashPrice)}*`));
        lines.push('');

        lines.push(t('💰 *Expected Savings*', '💰 *预计节省*', '💰 *Jimat Dijangka*'));
        if (pick.stLicenseApplies) {
            lines.push(t(`• Gross Monthly: ${fmt(pick.grossMonthlySaving)}`,
                         `• 月毛节省: ${fmt(pick.grossMonthlySaving)}`,
                         `• Jimat Kasar Bulanan: ${fmt(pick.grossMonthlySaving)}`));
            lines.push(t(`• ST License Fee: − ${fmt(pick.monthlyLicenseFee)} / mo (system >72 kW)`,
                         `• ST 执照费: − ${fmt(pick.monthlyLicenseFee)} / 月 (系统超过 72 kW)`,
                         `• Yuran Lesen ST: − ${fmt(pick.monthlyLicenseFee)} / bln (sistem >72 kW)`));
        }
        lines.push(t(`• ${pick.stLicenseApplies ? 'Net ' : ''}Monthly: ${fmt(pick.monthlySaving)} (${Math.round(pick.savingPct * 100)}% of bill)`,
                     `• ${pick.stLicenseApplies ? '净' : ''}每月: ${fmt(pick.monthlySaving)} (账单 ${Math.round(pick.savingPct * 100)}%)`,
                     `• ${pick.stLicenseApplies ? 'Bersih ' : ''}Bulanan: ${fmt(pick.monthlySaving)} (${Math.round(pick.savingPct * 100)}% bil)`));
        lines.push(t(`• Yearly: ${fmt(pick.monthlySaving * 12)}`,
                     `• 每年: ${fmt(pick.monthlySaving * 12)}`,
                     `• Tahunan: ${fmt(pick.monthlySaving * 12)}`));
        lines.push(t(`• 10-Year: ${fmt(pick.monthlySaving * 120)}`,
                     `• 10年: ${fmt(pick.monthlySaving * 120)}`,
                     `• 10 Tahun: ${fmt(pick.monthlySaving * 120)}`));
        lines.push(t(`• 20-Year: ${fmt(pick.monthlySaving * 240)}`,
                     `• 20年: ${fmt(pick.monthlySaving * 240)}`,
                     `• 20 Tahun: ${fmt(pick.monthlySaving * 240)}`));
        if (pick.roiYears != null) {
            const roiLabel = pick.stLicenseApplies ? ' (incl. License)' : '';
            lines.push(t(`• Payback (ROI)${roiLabel}: *${pick.roiYears} years*`,
                         `• 回本期 (ROI)${pick.stLicenseApplies ? ' (含执照)' : ''}: *${pick.roiYears} 年*`,
                         `• Pulangan (ROI)${pick.stLicenseApplies ? ' (termasuk Lesen)' : ''}: *${pick.roiYears} tahun*`));
            if (pick.stLicenseApplies && pick.roiYearsGross != null) {
                lines.push(t(`• Payback (excl. License): ${pick.roiYearsGross} years`,
                             `• 回本期 (不含执照): ${pick.roiYearsGross} 年`,
                             `• Pulangan (tanpa Lesen): ${pick.roiYearsGross} tahun`));
            }
        }
        lines.push('');

        lines.push(t('✅ *Includes*', '✅ *包括*', '✅ *Termasuk*'));
        const features = isCN ? [
            `✅ ${pick.panels}x Trina 全球Tier 1太阳能电板`,
            `✅ Inverter = ${pick.inverterKw}kW`,
            '✅ 包括安装费 & A-Z政府申请手续',
            '✅ 10年 全球Tier 1 Inverter保修',
            '✅ 15年 Trina 全球Tier 1太阳能电板保修',
            '✅ 30年 太阳能电板发电功效',
            '✅ 1年Maintenance保修',
        ] : isMY ? [
            `✅ ${pick.panels}x Panel Solar Trina (Global Tier 1)`,
            `✅ Inverter = ${pick.inverterKw}kW`,
            '✅ Pemasangan & Permohonan Kerajaan A-Z',
            '✅ Waranti 10 tahun Inverter (Global Tier 1)',
            '✅ Waranti 15 tahun Panel Solar Trina (Global Tier 1)',
            '✅ Jaminan Prestasi 30 tahun Panel Solar',
            '✅ Waranti Penyelenggaraan 1 tahun',
        ] : [
            `✅ ${pick.panels}x Trina Solar Panels (Global Tier 1)`,
            `✅ Inverter = ${pick.inverterKw}kW`,
            '✅ Installation & A-Z Government Application',
            '✅ 10-year Global Tier 1 Inverter Warranty',
            '✅ 15-year Trina Solar Panel Warranty (Global Tier 1)',
            '✅ 30-year Solar Panel Performance Guarantee',
            '✅ 1-year Maintenance Warranty',
        ];
        lines.push(...features);

        return lines.join('\n');
    }, [pick, companyName, bill, billKwh, tariffLabel, hoursLabel, operatingDays, maxDemand, phaseLabel, lang]);

    const isMobile = typeof window !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);

    const copyMsg = async () => {
        if (!message) return;
        try { await navigator.clipboard.writeText(message); } catch {}
        setCopyText('✅ Copied'); setIsCopied(true);
        setTimeout(() => { setCopyText('📋 Copy Message'); setIsCopied(false); }, 2500);
    };

    const openWA = async () => {
        if (!message) return;
        const encoded = encodeURIComponent(message);
        if (isMobile) {
            window.open(`https://wa.me/?text=${encoded}`, '_blank');
        } else {
            try { await navigator.clipboard.writeText(message); } catch {}
            window.open('https://web.whatsapp.com/', '_blank');
            setCopyText('✅ Copied — paste in WA'); setIsCopied(true);
            setTimeout(() => { setCopyText('📋 Copy Message'); setIsCopied(false); }, 3500);
        }
    };

    // Spec-row helper for the right panel
    const SpecRow = ({ label, value, color }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.45, borderBottom: `1px solid ${COLORS.border}` }}>
            <Typography sx={{ fontSize: '0.7rem', color: COLORS.sub, fontFamily: PP }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: color || COLORS.text, fontFamily: PP }}>{value}</Typography>
        </Box>
    );

    // Group-heading helper inside the proposal cards
    const GroupLabel = ({ children }) => (
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: PP, mt: 1, mb: 0.25 }}>
            {children}
        </Typography>
    );

    return (
        <Box sx={{ p: 2, backgroundColor: COLORS.bg }}>
            {/* Top controls — Language */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: COLORS.sub, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.75, fontFamily: PP }}>
                        WhatsApp Language
                    </Typography>
                    <ToggleButtonGroup exclusive value={lang} onChange={(_, v) => v && setLang(v)} size="small"
                        sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', py: 0.5, px: 1.5 },
                              '& .Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC) !important', color: 'white !important' } }}>
                        <ToggleButton value="en">🇬🇧 EN</ToggleButton>
                        <ToggleButton value="zh">🇨🇳 中文</ToggleButton>
                        <ToggleButton value="ms">🇲🇾 BM</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            <Grid container spacing={2}>
                {/* Inputs */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ p: 2.5, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', maxWidth: 340 }}>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text, fontFamily: PP, mb: 2 }}>
                            🏭 Site Profile
                        </Typography>

                        <Box sx={{ mb: 2 }}>
                            <Label>Company / Customer (Optional)</Label>
                            <TextField fullWidth size="small" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                                placeholder="e.g. Frontline Engineering"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Label required>Monthly Bill (RM)</Label>
                            <TextField fullWidth size="small" type="number" value={bill} onChange={(e) => setBill(e.target.value)}
                                placeholder="e.g. 8500"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                            {billKwh > 0 && (
                                <Typography sx={{ fontSize: '0.72rem', color: COLORS.primary, fontFamily: PP, mt: 0.5 }}>
                                    ⚡ Est. usage: {fmtKwh(billKwh)} / month
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Label required>Tariff Type</Label>
                            <FormControl fullWidth size="small">
                                <Select value={tariffRate} onChange={(e) => setTariffRate(Number(e.target.value))}
                                    sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                    {TARIFFS.map(t => (
                                        <MenuItem key={t.rate} value={t.rate} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                            {t.label} — RM {t.rate}/kWh
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Label required>Operating Hours</Label>
                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                <Select value={hoursPreset} onChange={(e) => handlePreset(e.target.value)}
                                    sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                    {HOUR_PRESETS.map(p => (
                                        <MenuItem key={p.key} value={p.key} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>{p.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {hoursPreset === 'custom' && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <Select value={opStart} onChange={(e) => setOpStart(Number(e.target.value))}
                                            sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                            {Array.from({ length: 24 }, (_, h) => (
                                                <MenuItem key={h} value={h} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                                    {String(h).padStart(2, '0')}:00
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography sx={{ fontFamily: PP, color: COLORS.sub, fontSize: '0.82rem' }}>to</Typography>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <Select value={opEnd} onChange={(e) => setOpEnd(Number(e.target.value))}
                                            sx={{ fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' }}>
                                            {Array.from({ length: 24 }, (_, i) => {
                                                const h = i + 1;
                                                return (
                                                    <MenuItem key={h} value={h} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                                                        {String(h).padStart(2, '0')}:00
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Label required>Operating Days / week</Label>
                            <TextField fullWidth size="small" type="number" inputProps={{ min: 1, max: 7 }} value={operatingDays}
                                onChange={(e) => setOperatingDays(Number(e.target.value))}
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Label>Off-Hours Baseline (%)</Label>
                            <TextField fullWidth size="small" type="number" inputProps={{ min: 0, max: 50, step: 1 }} value={offHourBaselinePct}
                                onChange={(e) => setOffHourBaselinePct(e.target.value)}
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                            <Typography sx={{ fontSize: '0.7rem', color: COLORS.sub, fontFamily: PP, mt: 0.5 }}>
                                % of monthly bill consumed outside op hours (overnight + weekends). Lowers the kWh solar can self-consume directly; the rest becomes grid import. Typical: 15–25%.
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Label required>Maximum Demand (kW)</Label>
                            <TextField fullWidth size="small" type="number" value={maxDemand} onChange={(e) => setMaxDemand(e.target.value)}
                                placeholder="e.g. 50"
                                InputProps={{ sx: { fontFamily: PP, fontSize: '0.9rem', borderRadius: '8px' } }} />
                            <Typography sx={{ fontSize: '0.7rem', color: COLORS.sub, fontFamily: PP, mt: 0.5 }}>
                                Max kW the site can install. Caps inverter size and kWp.
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Label>Phase</Label>
                            <ToggleButtonGroup exclusive fullWidth value={phaseMode} onChange={(_, v) => v && setPhaseMode(v)} size="small"
                                sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', py: 0.6 },
                                      '& .Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC) !important', color: 'white !important' } }}>
                                <ToggleButton value="single">1-Phase</ToggleButton>
                                <ToggleButton value="3phase">3-Phase</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Box>
                            <Label>Inverter Brand</Label>
                            <ToggleButtonGroup exclusive fullWidth value={brand} onChange={(_, v) => v && setBrand(v)} size="small"
                                sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', py: 0.6 },
                                      '& .Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC) !important', color: 'white !important' } }}>
                                <ToggleButton value="solis">Solis<br /><span style={{ fontSize: '0.65rem', fontWeight: 400 }}>String</span></ToggleButton>
                                <ToggleButton value="goodwe">GoodWe<br /><span style={{ fontSize: '0.65rem', fontWeight: 400 }}>Hybrid</span></ToggleButton>
                            </ToggleButtonGroup>
                            {huaweiForced && (
                                <Typography sx={{ fontSize: '0.7rem', color: COLORS.warning, fontFamily: PP, mt: 0.5 }}>
                                    ⚠️ System ≥ 100 kWp — auto-switched to Huawei (String).
                                </Typography>
                            )}
                        </Box>
                    </Card>
                </Grid>

                {/* Output column — Math cards stacked above the result */}
                <Grid item xs={12} md={9}>
                    {/* Sizing Math */}
                    {breakdown && billKwh > 0 && (
                        <Card sx={{ mb: 2, p: 2, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', backgroundColor: '#FFFBEB', border: '1px solid #FEF3C7' }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400E', fontFamily: PP, mb: 1 }}>
                                📊 Sizing Math
                            </Typography>
                            <Box sx={{ '& > div': { fontSize: '0.72rem', fontFamily: PP, color: '#78350F', mb: 0.3, display: 'flex', justifyContent: 'space-between' } }}>
                                <Box><span>Monthly kWh =</span><span>{fmtKwh(breakdown.monthlyKwh)}</span></Box>
                                <Box><span>Op Days/Month =</span><span>{operatingDays} × 4.345 = {breakdown.workdaysPerMonth.toFixed(1)}</span></Box>
                                <Box><span>Daily kWh (op) =</span><span>{fmtKwh(breakdown.dailyKwh)}</span></Box>
                                <Box><span>Required kWp =</span><span>{breakdown.dailyKwh.toFixed(0)} ÷ 3.4 = {breakdown.requiredKwp.toFixed(2)} kWp</span></Box>
                                <Box><span>Required Panels =</span><span>{breakdown.requiredKwp.toFixed(2)} ÷ 0.64 = {breakdown.requiredPanels}</span></Box>
                                <Box><span>Inverter (from MD) =</span><span>floor({maxDemand || '—'} ÷ 5) × 5 = {breakdown.inverterKw} kW</span></Box>
                                <Box><span>Max kWp by Inverter =</span><span>{breakdown.inverterKw} × {breakdown.ratioCap} = {breakdown.maxKwpByInv.toFixed(2)} kWp ({breakdown.maxPanelsByInv} panels)</span></Box>
                                <Box sx={{ pt: 0.5, mt: 0.5, borderTop: '1px solid #FCD34D', fontWeight: 700 }}>
                                    <span>→ Target =</span><span>{breakdown.targetPanels} panels ({(breakdown.targetPanels * 0.64).toFixed(2)} kWp) · {breakdown.inverterKw} kW inv</span>
                                </Box>
                                {pick && (
                                    <>
                                        <Box sx={{ mt: 0.5 }}><span>Solar window =</span><span>08:00 — 18:00 (10h)</span></Box>
                                        <Box><span>Op overlap =</span><span>{pick.overlapHours}h → {Math.round(pick.workdaySelfPct * 100)}% workday self-consumed</span></Box>
                                    </>
                                )}
                            </Box>
                        </Card>
                    )}

                    {/* Pricing Math */}
                    {pick?.priceBreakdown && (
                        <Card sx={{ mb: 2, p: 2, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', backgroundColor: '#EFF6FF', border: '1px solid #DBEAFE' }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1E3A8A', fontFamily: PP, mb: 1 }}>
                                💵 Pricing Math · {pick.brandLabel}
                            </Typography>
                            <Box sx={{ '& > div': { fontSize: '0.72rem', fontFamily: PP, color: '#1E3A8A', mb: 0.3, display: 'flex', justifyContent: 'space-between' } }}>
                                {pick.priceBreakdown.basis === 'residential' ? (
                                    <>
                                        <Box><span>System size =</span><span>{pick.panels} panels = {pick.kwp} kWp</span></Box>
                                        <Box><span>Residential price ({pick.panels} panels) =</span><span>{fmt(pick.priceBreakdown.residentialCash)}</span></Box>
                                        {pick.priceBreakdown.discountPerKwp > 0 ? (
                                            <>
                                                <Box><span>{pick.brandLabel} discount =</span><span>RM {pick.priceBreakdown.discountPerKwp} × {pick.kwp} kWp = {fmt(pick.priceBreakdown.discountTotal)}</span></Box>
                                                <Box sx={{ pt: 0.5, mt: 0.5, borderTop: '1px solid #BFDBFE', fontWeight: 700 }}>
                                                    <span>→ Final Price =</span><span>{fmt(pick.priceBreakdown.residentialCash)} − {fmt(pick.priceBreakdown.discountTotal)} = {fmt(pick.priceBreakdown.finalPrice)}</span>
                                                </Box>
                                            </>
                                        ) : (
                                            <Box sx={{ pt: 0.5, mt: 0.5, borderTop: '1px solid #BFDBFE', fontWeight: 700 }}>
                                                <span>→ Final Price =</span><span>{fmt(pick.priceBreakdown.finalPrice)} (no brand discount)</span>
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Box><span>System size =</span><span>{pick.panels} panels = {pick.kwp} kWp</span></Box>
                                        <Box><span>Tier ({pick.priceBreakdown.tierLabel}) =</span><span>RM {pick.priceBreakdown.ratePerKwp.toLocaleString('en-MY')} / kWp</span></Box>
                                        <Box sx={{ pt: 0.5, mt: 0.5, borderTop: '1px solid #BFDBFE', fontWeight: 700 }}>
                                            <span>→ Final Price =</span><span>RM {pick.priceBreakdown.ratePerKwp.toLocaleString('en-MY')} × {pick.kwp} = {fmt(pick.priceBreakdown.finalPrice)}</span>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Card>
                    )}

                    {!maxSaving ? (
                        <Card sx={{ p: 4, textAlign: 'center', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <Typography sx={{ fontFamily: PP, color: COLORS.sub, fontSize: '0.9rem' }}>
                                Enter bill and max demand to auto-generate proposals.
                            </Typography>
                            <Typography sx={{ fontFamily: PP, color: COLORS.sub, fontSize: '0.75rem', mt: 1 }}>
                                Systems sized between 1 panel and Max Demand × {breakdown?.ratioCap || 1.35}.
                            </Typography>
                        </Card>
                    ) : (
                        <>
                            {(() => {
                                const cardCount = 1 + (fastestRoi ? 1 : 0) + (targetFifty ? 1 : 0) + (selco ? 1 : 0);
                                // 4 cards → 2×2 grid (md=6). 3 → 1 row (md=4). 2 → side-by-side (md=6). 1 → full (md=12).
                                const md = cardCount === 4 ? 6 : cardCount === 3 ? 4 : cardCount === 2 ? 6 : 12;
                                return (
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} md={md}>
                                            <ProposalCard data={maxSaving} accent={COLORS.success} headerLabel="💰 Max Saving · ATAP" fmt={fmt} SpecRow={SpecRow} GroupLabel={GroupLabel} />
                                        </Grid>
                                        {fastestRoi && (
                                            <Grid item xs={12} md={md}>
                                                <ProposalCard data={fastestRoi} accent={COLORS.primary} headerLabel="⚡ Fastest ROI · ATAP" fmt={fmt} SpecRow={SpecRow} GroupLabel={GroupLabel} />
                                            </Grid>
                                        )}
                                        {targetFifty && (
                                            <Grid item xs={12} md={md}>
                                                <ProposalCard data={targetFifty} accent={COLORS.warning} headerLabel="💵 50% Saved · ATAP" fmt={fmt} SpecRow={SpecRow} GroupLabel={GroupLabel} />
                                            </Grid>
                                        )}
                                        {selco && (
                                            <Grid item xs={12} md={md}>
                                                <ProposalCard data={selco} accent="#7C3AED" headerLabel="🔌 SELCo · Zero Export" fmt={fmt} SpecRow={SpecRow} GroupLabel={GroupLabel} />
                                            </Grid>
                                        )}
                                    </Grid>
                                );
                            })()}

                            {!fastestRoi && (
                                <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, mb: 2, textAlign: 'center' }}>
                                    No smaller system reaches ≥ 50% bill offset within the MD cap.
                                </Typography>
                            )}

                            <Card sx={{ borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                {(fastestRoi || targetFifty || selco) && (
                                    <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: COLORS.sub, fontFamily: PP, mr: 1 }}>
                                            WhatsApp uses:
                                        </Typography>
                                        <ToggleButtonGroup exclusive value={selected} onChange={(_, v) => v && setSelected(v)} size="small"
                                            sx={{ '& .MuiToggleButton-root': { fontFamily: PP, fontSize: '0.75rem', textTransform: 'none', py: 0.4, px: 1.2 },
                                                  '& .Mui-selected': { background: 'linear-gradient(135deg,#1A8CFF,#0F62CC) !important', color: 'white !important' } }}>
                                            <ToggleButton value="maxSaving">Max Saving</ToggleButton>
                                            {fastestRoi && <ToggleButton value="fastestRoi">Fastest ROI</ToggleButton>}
                                            {targetFifty && <ToggleButton value="targetFifty">50% Saved</ToggleButton>}
                                            {selco && <ToggleButton value="selco">SELCo</ToggleButton>}
                                        </ToggleButtonGroup>
                                        <Chip label={lang === 'en' ? 'EN 🇬🇧' : lang === 'zh' ? '中文 🇨🇳' : 'BM 🇲🇾'} size="small"
                                            sx={{ fontFamily: PP, fontWeight: 600, fontSize: '0.72rem', borderRadius: 6, backgroundColor: COLORS.light, color: COLORS.dark, ml: 'auto' }} />
                                    </Box>
                                )}

                                <Box sx={{ m: 2, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
                                    <Box sx={{ backgroundColor: '#075e54', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ width: 36, height: 36, backgroundColor: '#128c7e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>☀️</Box>
                                        <Box>
                                            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem', fontFamily: PP }}>Solar Sales</Typography>
                                            <Typography sx={{ color: '#80cbc4', fontSize: '0.75rem', fontFamily: PP }}>WhatsApp Business</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ backgroundColor: '#e5ddd5', maxHeight: 360, overflowY: 'auto', p: 1.5 }}>
                                        <Box sx={{ backgroundColor: '#dcf8c6', borderRadius: '8px', p: 1.5, fontFamily: PP, fontSize: '0.78rem', whiteSpace: 'pre-wrap', color: '#111' }}>
                                            {message}
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', backgroundColor: '#F8FAFC' }}>
                                    <Button onClick={copyMsg} variant="outlined" size="small" sx={{
                                        fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem', boxShadow: 'none',
                                        borderColor: isCopied ? COLORS.success : COLORS.border,
                                        color: isCopied ? COLORS.success : COLORS.text,
                                        backgroundColor: isCopied ? '#D1FAE5' : 'white',
                                    }}>{copyText}</Button>
                                    <Button onClick={openWA} variant="contained" size="small" sx={{
                                        fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem',
                                        backgroundColor: COLORS.success, boxShadow: 'none', '&:hover': { backgroundColor: '#059669' },
                                    }}>📲 Send WhatsApp</Button>
                                    <Typography sx={{ fontSize: '0.72rem', color: COLORS.sub, fontFamily: PP, ml: { xs: 0, md: 'auto' }, display: { xs: 'none', md: 'block' } }}>
                                        Desktop: auto-copies & opens WhatsApp Web
                                    </Typography>
                                </Box>
                            </Card>
                        </>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}
